import React from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import NativeSelectField from '../../FormFields/NativeSelectField';

/** Labels sit above their control, the way they do on the panes these rows summarise. */
export const SUMMARY_LABEL_SX = { display: 'block', mb: 0.5 };

const INCLUDE_EXCLUDE_OPTIONS = [
  { value: '', option: 'None' },
  { value: 'Include', option: 'Include' },
  { value: 'Exclude', option: 'Exclude' },
];

/**
 * One read-only row of the summary: a label over the saved value.
 *
 * A read-only field rather than a disabled one — nothing here can ever be enabled, and a disabled
 * control reads as "temporarily off" rather than "this is what was saved".
 */
export function SummaryField({ label = undefined, value, placeholder = undefined, multiline = false }) {
  return (
    <Box>
      {label && (
        <FormLabel component='p' sx={SUMMARY_LABEL_SX}>
          {label}
        </FormLabel>
      )}
      <TextField
        value={value ?? ''}
        placeholder={placeholder}
        multiline={multiline}
        minRows={multiline ? 3 : undefined}
        slotProps={{ input: { readOnly: true } }}
        sx={{ '& .MuiInputBase-input': { color: 'text.secondary' } }}
      />
    </Box>
  );
}

export const SwitchRow = ({ label, value }) =>
  value ? (
    <Grid container spacing={2} sx={{ alignItems: 'center' }}>
      <Grid size={{ xs: 12, sm: 6 }}>
        <FormLabel component='span'>{label}</FormLabel>
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <FormControlLabel control={<Switch checked={!!value} disabled />} label='' sx={{ m: 0 }} />
      </Grid>
    </Grid>
  ) : null;

export function SelectSummary({ value, label, options = INCLUDE_EXCLUDE_OPTIONS, children }) {
  return value ? (
    <Grid container spacing={2} sx={{ mt: 1, alignItems: 'start' }}>
      <Grid size={{ xs: 12, lg: 3 }}>
        <Typography variant='h5'>{label}</Typography>
      </Grid>
      <Grid size={3}>
        <SelectInput value={value} options={options} />
      </Grid>
      {children}
    </Grid>
  ) : null;
}

/** Chips rather than a disabled picker: these are saved values, not a control someone can open. */
export const MultiselectSummary = ({ value, textField = 'name' }) => {
  if (!value) return null;
  const items = Array.isArray(value) ? value : [value];
  if (items.length === 0) return null;

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 0.5,
        minHeight: 40,
        px: 1.5,
        py: 1,
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'action.hover',
      }}
    >
      {items.map((item, index) => {
        const label = item && typeof item === 'object' ? (item[textField] ?? item.id ?? '') : item;
        return <Chip key={`${label}-${index}`} size='small' label={String(label)} />;
      })}
    </Box>
  );
};

export const SelectInput = ({ value, options = INCLUDE_EXCLUDE_OPTIONS }) => (
  <NativeSelectField
    value={value ?? ''}
    onChange={() => null}
    disabled
    slotProps={{ select: { native: true } }}
  >
    {options.map((option) => (
      <option key={option.value} value={option.value}>
        {option.option}
      </option>
    ))}
  </NativeSelectField>
);

export function SelectRow({ value, label, options = INCLUDE_EXCLUDE_OPTIONS, children }) {
  return (
    <Grid container spacing={2} sx={{ mt: 1, alignItems: 'start' }}>
      <Grid size={{ xs: 12, lg: 3 }}>
        <Typography variant='h5'>{label}</Typography>
      </Grid>
      <Grid size={{ xs: 3, lg: 3 }}>
        <SelectInput value={value} options={options} />
      </Grid>
      {children}
    </Grid>
  );
}

export function InputSummary({ value }) {
  return <SummaryField value={value} />;
}
